import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";
import { graphql, useStaticQuery } from "gatsby";
import AltchaWidget from "../utility/altcha";
import dayjs from "dayjs";
import debounce from "lodash.debounce";
import withPreview from "../utility/with-preview";
import Breadcrumb from "./breadcrumb";
import { GroupProvider } from "../context/group";
import FormSelect from "./elements/form-select";
import FormInput from "./elements/form-input";
import FormText from "./elements/form-text";
import FormAutocomplete from "./elements/form-autocomplete";
import FormDatePicker from "./elements/form-datepicker";
import {
  validateEmail,
  validateTel,
  formatRussianMobile,
  validateRussianMobile,
} from "../utility/formatters";

// https://www.haier.com/ru/servicesupport/repairs/repair_research/
const API_FORM_URL = process.env.GATSBY_YANDEX_API_FORM_URL;

const changeFormatters = {
  mobile_phone: formatRussianMobile,
};

const RequestRepairForm = ({ data, breadcrumb }) => {
  const { repairRequest, serviceInformation, contactInformation, address, returnSubmitForm } = data;

  const { labelManagerLabels } = useStaticQuery(graphql`
    {
      labelManagerLabels: labelManagerLabels {
        validDate
        validEmail
        validNumber
        send
        requiredFields
      }
    }
  `);

  const [formData, setFormData] = useState({
    serial_number: "",
    brand: "",
    category: "",
    product_model: "",
    purchase_date: "",
    preferred_visit_date: "",
    preferred_time: serviceInformation?.desiredTime?.[0],
    description: "",
    first_name: "",
    last_name: "",
    email: "",
    mobile_phone: "",
    home_phone: "",
    country: "",
    detail_address: "",
    region: "",
    district: "",
    town: "",
  });

  const [captcha, setCaptcha] = useState(null);
  const altchaRef = useRef();
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const onChange = useCallback((e) => {
    const { name } = e.target;
    // console.log('onChange: ', value, checked, type)
    setFormData((current) => {
      const { name, value, type, checked } = e.target;
      const formatter = changeFormatters[name];
      const newValue =
        type === "checkbox" ? checked : formatter ? formatter(e, current[name]) : value;
      return {
        ...current,
        [name]: newValue,
      };
    });
    setFormErrors((current) => (current[name] ? { ...current, [name]: false } : current));
  }, []);

  const formDataRef = useRef({});
  formDataRef.current = formData;

  const [success, setSuccess] = useState(false);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const form = e.currentTarget;
      const formElements = [...form.elements].filter(
        (el) => el.tagName !== "BUTTON" && el.id !== "g-recaptcha-response"
      );
      const missingFields = formElements.filter(
        (field) =>
          field.getAttribute &&
          field.getAttribute("data-required") === "true" &&
          (!field.value ||
            (field.type === "checkbox" && !field.checked) ||
            field.getAttribute("data-invalid") === "true")
      );

      const invalidFields = [];
      if (form.email.value && !validateEmail(form.email.value)) {
        invalidFields.push("email");
      }
      if (form.home_phone.value && !validateTel(form.home_phone.value)) {
        invalidFields.push("home_phone");
      }
      if (form.mobile_phone.value && !validateRussianMobile(form.mobile_phone.value)) {
        invalidFields.push("mobile_phone");
      }

      if (missingFields.length || invalidFields.length) {
        const missingErrs = missingFields.reduce(
          (res, field) => ({
            ...res,
            [field.name]: labelManagerLabels.requiredFields,
          }),
          {}
        );
        const errors = invalidFields.reduce(
          (res, field) => ({ ...res, [field]: true }),
          missingErrs
        );
        setFormErrors((current) => ({ ...current, ...errors }));
        console.warn("Cannot submit: ", errors);
        const firstElError = formElements.find(
          (el) =>
            missingFields.some((field) => el.name === field.name) || invalidFields.includes(el.name)
        );
        firstElError.scrollIntoView({ behavior: "smooth" });
        return;
      }

      setLoading(true);
      setServerError(false);

      try {
        const formData = formDataRef.current;
        const body = {
          ...formData,
          // Fix
          mobile_phone: formData.mobile_phone.replace(
            /^8\((\d{3})\)(\d{3})-(\d{2})-(\d{2})$/,
            "8$1$2$3$4"
          ),
          captcha,
        };
        console.log("About to submit: ", body);
        const response = await fetch(`${API_FORM_URL}/add-request-repair`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });
        console.log("response: ", response);
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        const result = await response.json();
        console.log("result: ", result);
        setSuccess(true);
      } catch (err) {
        setServerError(true);
        console.error("Error submitting: ", err);
        altchaRef.current?.reset();
        setCaptcha(null);
      }
      setLoading(false);
    },
    [captcha, labelManagerLabels]
  );

  const formInfo = useMemo(
    () => ({
      formData,
      onChange,
      formErrors,
      setFormErrors,
    }),
    [formData, onChange, formErrors, setFormErrors]
  );

  const [brandOptions, setbrandOptions] = useState(
    () =>
      repairRequest?.brand?.map((item) => ({
        label: item,
        value: item,
      })) || []
  );
  const [productCategoryOptions, setProductCategoryOptions] = useState(
    () =>
      repairRequest?.productCategory.map((item) => ({
        label: item,
        value: item,
      })) || []
  );

  useEffect(() => {
    const loadBrands = async () => {
      try {
        const res = await fetch(`${API_FORM_URL}/get-requests-repair-brand-category`);
        if (!res.ok) {
          throw new Error(`Err ${res.status} ${res.statusText}`);
        }
        const data = await res.json();
        console.log("brand and category result: ", data);
        const brands = [...data.brand].sort((a, b) =>
          Object.keys(a)[0] === "HAIER" ? -1 : Object.keys(b)[0] === "HAIER" ? 1 : 0
        );
        const categories = [...data.category].sort((a, b) =>
          Object.keys(a)[0] === "Cooking" ? -1 : Object.keys(b)[0] === "Cooking" ? 1 : 0
        );
        setbrandOptions(
          brands
            ?.map((item) => {
              const [entry] = Object.entries(item);
              const [value, label] = entry || [];
              return entry
                ? {
                    label,
                    value,
                  }
                : null;
            })
            .filter(Boolean)
        );
        setProductCategoryOptions(
          categories
            ?.map((item) => {
              const [entry] = Object.entries(item);
              const [value, label] = entry || [];
              return entry
                ? {
                    label,
                    value,
                  }
                : null;
            })
            .filter(Boolean)
        );
        setFormData((current) => {
          const [brandDef] = brands?.[0] ? Object.entries(brands?.[0])?.[0] : [];
          const [categoryDef] = categories?.[0] ? Object.entries(categories?.[0])?.[0] : [];
          return {
            ...current,
            brand: brandDef,
            category: categoryDef,
          };
        });
      } catch (err) {
        console.error("Error loading brands and categories: ", err);
      }
    };
    loadBrands();
  }, []);

  const { brand, category, country, region, district } = formData;

  const [modelOptions, setModelOptions] = useState();
  useEffect(() => {
    if (brand && category) {
      const loadModels = async () => {
        try {
          const params = new URLSearchParams();
          params.append("brand", brand);
          params.append("category", category);
          const res = await fetch(`${API_FORM_URL}/get-requests-repair-model?${params.toString()}`);
          if (!res.ok) {
            throw new Error(`Err ${res.status} ${res.statusText}`);
          }
          const data = await res.json();
          console.log("model result: ", data);
          const options = data.model;
          setModelOptions(options?.length ? options : []);
          setFormData((current) => ({
            ...current,
            product_model: "",
          }));
        } catch (err) {
          console.error("Error loading models: ", err);
        }
      };
      loadModels();
    }
  }, [brand, category]);

  const [stateOptions, setStateOptions] = useState();
  useEffect(() => {
    if (country) {
      const loadStates = async () => {
        try {
          const params = new URLSearchParams();
          params.append("country", country);
          const res = await fetch(`${API_FORM_URL}/get-requests-repair-state?${params.toString()}`);
          if (!res.ok) {
            throw new Error(`Err ${res.status} ${res.statusText}`);
          }
          const data = await res.json();
          console.log("state result: ", data);
          const options = data.state;
          setStateOptions(options?.length ? options : []);
          setFormData((current) => ({
            ...current,
            region: "",
          }));
        } catch (err) {
          console.error("Error loading states: ", err);
        }
      };
      loadStates();
    }
  }, [country]);

  const [districtOptions, setDistrictOptions] = useState();
  useEffect(() => {
    if (country && region) {
      const loadDistricts = async () => {
        try {
          const params = new URLSearchParams();
          params.append("country", country);
          params.append("state", region);
          const res = await fetch(
            `${API_FORM_URL}/get-requests-repair-district?${params.toString()}`
          );
          if (!res.ok) {
            throw new Error(`Err ${res.status} ${res.statusText}`);
          }
          const data = await res.json();
          console.log("districts result: ", data);
          const options = data.district;
          setDistrictOptions(options?.length ? options : []);
          setFormData((current) => ({
            ...current,
            district: "",
            town: "",
          }));
        } catch (err) {
          console.error("Error loading districts: ", err);
        }
      };
      loadDistricts();
    }
  }, [country, region]);

  const [cityOptions, setCityOptions] = useState();
  useEffect(() => {
    if (country && region && district) {
      const loadCities = async () => {
        try {
          const params = new URLSearchParams();
          params.append("country", country);
          params.append("state", region);
          // if (district) {
          params.append("district", district);
          // }
          const res = await fetch(`${API_FORM_URL}/get-requests-repair-city?${params.toString()}`);
          if (!res.ok) {
            throw new Error(`Err ${res.status} ${res.statusText}`);
          }
          const data = await res.json();
          console.log("city result: ", data);
          const options = data.city;
          setCityOptions(options?.length ? options : []);
          setFormData((current) => ({
            ...current,
            town: "",
          }));
        } catch (err) {
          console.error("Error loading cities: ", err);
        }
      };
      loadCities();
    }
  }, [country, region, district]);

  const refineCityOptions = useCallback(async (country, region, cityPart) => {
    try {
      const params = new URLSearchParams();
      params.append("country", country);
      params.append("state", region);
      params.append("city", cityPart);
      const res = await fetch(`${API_FORM_URL}/get-requests-repair-city?${params.toString()}`);
      if (!res.ok) {
        throw new Error(`Err ${res.status} ${res.statusText}`);
      }
      const data = await res.json();
      console.log("city result: ", data);
      const options = data.city?.sort((a, b) =>
        a.startsWith(cityPart) === b.startsWith(cityPart)
          ? a < b
            ? -1
            : 1
          : a.startsWith(cityPart)
          ? -1
          : 1
      );
      setCityOptions(options?.length ? options : []);
      setFormData((current) => ({
        ...current,
        town: "",
      }));
    } catch (err) {
      console.error("Error loading cities from option: ", err);
    }
  }, []);
  const debounceRefineCityOptions = useMemo(
    () => debounce(refineCityOptions, 300),
    [refineCityOptions]
  );

  const onRequestOptions = useCallback(
    (part) => {
      console.log("onRequestOptions: ", part);
      if (district || part.length < 2) return;
      debounceRefineCityOptions(country, region, part);
    },
    [country, region, district, debounceRefineCityOptions]
  );

  const desiredTimeOptions = useMemo(
    () =>
      serviceInformation?.desiredTime.map((item) => ({
        label: item,
        value: item,
      })) || [],
    [serviceInformation]
  );

  const [countriesOptions, setCountriesOptions] = useState(
    address?.country?.map((item) => ({
      label: item,
      value: item,
    })) || []
  );

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const res = await fetch(`${API_FORM_URL}/get-requests-repair-country`);
        if (!res.ok) {
          throw new Error(`Err ${res.status} ${res.statusText}`);
        }
        const data = await res.json();
        console.log("country result: ", data);
        const countries = [...data.country].sort((a, b) =>
          Object.keys(a)[0] === "Russia" ? -1 : Object.keys(b)[0] === "Russia" ? 1 : 0
        );
        setCountriesOptions(
          countries
            ?.map((item) => {
              const [entry] = Object.entries(item);
              const [value, label] = entry || [];
              return entry
                ? {
                    label,
                    value,
                  }
                : null;
            })
            .filter(Boolean)
        );
        setFormData((current) => {
          const [countryDef] = countries?.[0] ? Object.entries(countries?.[0])?.[0] : [];
          return {
            ...current,
            country: countryDef,
          };
        });
      } catch (err) {
        console.error("Error loading countries: ", err);
      }
    };
    loadCountries();
  }, []);

  return (
    <section className="section">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 py-3">
            {breadcrumb && <Breadcrumb className="breadcrumb--dark" breadcrumb={breadcrumb} />}
          </div>
        </div>
      </div>
      {success ? (
        <div className="container">
          <div className="row">
            <div className="col-12 py-5">
              <div dangerouslySetInnerHTML={{ __html: returnSubmitForm }}></div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="container">
            <div className="row">
              <div className="col-12 mt-3">
                <h2>{repairRequest.repairRequestLabel}</h2>
              </div>
            </div>
          </div>
          <form className="form form--request-repair" onSubmit={onSubmit}>
            <GroupProvider data={formInfo}>
              <div className="container">
                <div className="row">
                  <div className="col-md-6">
                    <FormInput
                      id="serial_number"
                      field="serial_number"
                      maxLength={40}
                      label={repairRequest?.serialNumberLabel}
                    />
                  </div>
                  <div className="col-md-6">
                    <FormSelect
                      id="brand"
                      field="brand"
                      label={repairRequest?.brandLabel}
                      options={brandOptions}
                      isRequired={true}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <FormSelect
                      id="category"
                      field="category"
                      label={repairRequest?.productCategoryLabel}
                      options={productCategoryOptions}
                      isRequired={true}
                    />
                  </div>
                  <div className="col-md-6">
                    <FormAutocomplete
                      id="product_model"
                      field="product_model"
                      label={repairRequest?.modelLabel}
                      options={modelOptions}
                      emptyMsg={repairRequest?.modelLabelNoResult}
                      isRequired={true}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <FormDatePicker
                      id="purchase_date"
                      field="purchase_date"
                      maxLength={10}
                      maxDate={dayjs().format("MM/DD/YYYY")}
                      label={`${repairRequest?.purchaseDateLabel} *`}
                      isRequired={true}
                      placeholder="MM/DD/YYYY"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 mt-3">
                    <h2>{serviceInformation?.serviceInformationLabel}</h2>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <FormDatePicker
                      id="preferred_visit_date"
                      field="preferred_visit_date"
                      maxLength={10}
                      label={serviceInformation?.desiredDateOfVisitLabel}
                      placeholder="MM/DD/YYYY"
                      minDate={dayjs().format("MM/DD/YYYY")}
                    />
                  </div>
                  <div className="col-md-6">
                    <FormSelect
                      id="preferred_time"
                      field="preferred_time"
                      label={serviceInformation?.desiredTimeLabel}
                      options={desiredTimeOptions}
                    />
                  </div>
                  <div className="col-12 mb-4">
                    <span className="form__info">
                      {serviceInformation?.noteOfServiceInformation}
                    </span>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <FormText
                      id="description"
                      field="description"
                      className="mb-2"
                      maxLength={200}
                      label={`${serviceInformation?.applicationLabel} *`}
                      isRequired={true}
                      rows={4}
                    />
                    <div className="form__info">{serviceInformation?.applicationNote}</div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 mt-3">
                    <h2>{contactInformation?.contactInformationLabel}</h2>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <FormInput
                      id="first_name"
                      field="first_name"
                      maxLength={40}
                      label={`${contactInformation?.nameLabel} *`}
                      isRequired={true}
                    />
                  </div>
                  <div className="col-md-6">
                    <FormInput
                      id="last_name"
                      field="last_name"
                      maxLength={80}
                      label={`${contactInformation?.surnameLabel} *`}
                      isRequired={true}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <FormInput
                      id="mobile_phone"
                      field="mobile_phone"
                      maxLength={40}
                      label={`${contactInformation?.telMobLabel} *`}
                      isRequired={true}
                      placeholder="8(_ _ _) _ _ _- _ _ - _ _"
                      validate={validateRussianMobile}
                      formatWarning={labelManagerLabels.validNumber}
                    />
                  </div>
                  <div className="col-md-6">
                    <FormInput
                      type="tel"
                      id="home_phone"
                      field="home_phone"
                      maxLength={40}
                      label={contactInformation?.telHomeLabel}
                      validate={validateTel}
                      formatWarning={labelManagerLabels.validNumber}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <FormInput
                      id="email"
                      field="email"
                      maxLength={80}
                      label={`${contactInformation?.emailAddressLabel} *`}
                      isRequired={true}
                      validate={validateEmail}
                      formatWarning={labelManagerLabels.validEmail}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 mt-3">
                    <h2>{address?.locationLabel}</h2>
                    <div className="form__info mb-4">{address?.locationNote}</div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <div className="row">
                      <div className="col-md-4">
                        <FormSelect
                          id="country"
                          field="country"
                          label={address?.countryLabel}
                          options={countriesOptions}
                          isRequired={true}
                        />
                      </div>
                      <div className="col-md-4">
                        <FormAutocomplete
                          id="region"
                          field="region"
                          label={address?.regionLabel}
                          options={stateOptions}
                          emptyMsg="No results"
                          isRequired={true}
                        />
                      </div>
                      <div className="col-md-4">
                        <FormAutocomplete
                          id="district"
                          field="district"
                          label={address?.districtLabel}
                          options={districtOptions}
                          emptyMsg="No results"
                        />
                      </div>
                      <div className="col-md-4">
                        <FormAutocomplete
                          id="town"
                          field="town"
                          label={address?.townLabel}
                          options={cityOptions}
                          onRequestOptions={onRequestOptions}
                          emptyMsg="No results"
                          isRequired={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <FormInput
                      id="detail_address"
                      field="detail_address"
                      maxLength={40}
                      label={`${address?.detailedAddressLabel} *`}
                      isRequired={true}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <FormText
                      id="note"
                      field="note"
                      maxLength={500}
                      placeholder={address?.notePlaceholder}
                      label={address?.note}
                      rows={4}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <div className="my-5">
                      <AltchaWidget ref={altchaRef} onChange={setCaptcha} />
                    </div>
                    <div className="text-center">
                      <button className="btn-link" disabled={!captcha || loading}>
                        {labelManagerLabels.send}
                      </button>
                      {serverError && (
                        <p className="mt-2 form__service-not-available">
                          {labelManagerLabels.serviceNotAvailable}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </GroupProvider>
          </form>
        </>
      )}
    </section>
  );
};

export default withPreview(RequestRepairForm);
export const fragment = graphql`
  fragment RequestRepairFormFragment on LiferayRequestRepairForm {
    id
    liferayFields {
      siteId
      articleId
    }
    repairRequest {
      repairRequestLabel
      serialNumberLabel
      modelLabelNoResult
      brandLabel
      brand
      productCategoryLabel
      productCategory
      modelLabel
      purchaseDateLabel
    }
    contactInformation {
      contactInformationLabel
      nameLabel
      emailAddressLabel
      surnameLabel
      telMobLabel
      telHomeLabel
    }
    serviceInformation {
      serviceInformationLabel
      desiredTimeLabel
      desiredTime
      desiredDateOfVisitLabel
      noteOfServiceInformation
      applicationLabel
      applicationNote
    }
    address {
      locationLabel
      countryLabel
      country
      regionLabel
      districtLabel
      townLabel
      detailedAddressLabel
      locationNote
      note
      notePlaceholder
    }
    returnSubmitForm
  }
`;
